import React, { useState } from 'react';
import {
  FiShield,
  FiAlertTriangle,
  FiAlertCircle,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiZap,
  FiLock,
  FiSearch,
} from 'react-icons/fi';
import { IconType } from 'react-icons';

export interface SafetyTestType {
  id: string;
  name: string;
  description: string;
  icon: IconType;
  categories: string[];
}

export const SAFETY_TEST_TYPES: SafetyTestType[] = [
  {
    id: 'toxicity',
    name: 'Toxicity Detection',
    description: 'Detect harmful, offensive, or toxic content in outputs',
    icon: FiZap,
    categories: ['hate', 'harassment', 'violence', 'self_harm', 'sexual'],
  },
  {
    id: 'pii',
    name: 'PII Detection',
    description: 'Detect personally identifiable information leakage',
    icon: FiLock,
    categories: ['email', 'phone', 'ssn', 'credit_card', 'name', 'address'],
  },
  {
    id: 'bias',
    name: 'Bias Detection',
    description: 'Detect demographic or cultural biases in responses',
    icon: FiShield,
    categories: ['gender', 'race', 'age', 'religion', 'political'],
  },
  {
    id: 'jailbreak',
    name: 'Jailbreak Detection',
    description: 'Detect prompt injection and jailbreak attempts',
    icon: FiAlertCircle,
    categories: ['prompt_leak', 'instruction_override', 'role_escape'],
  },
];

export interface SafetyFailThreshold {
  value: string;
  label: string;
  description: string;
}

export const SAFETY_FAIL_THRESHOLDS: SafetyFailThreshold[] = [
  { value: 'any', label: 'Any Issue', description: 'Fail on any safety issue detected' },
  {
    value: 'high',
    label: 'High Severity',
    description: 'Fail only on high or critical severity issues',
  },
  {
    value: 'critical',
    label: 'Critical Only',
    description: 'Fail only on critical severity issues',
  },
];

type SeverityLevel = 'critical' | 'high' | 'medium' | 'low';

interface SeverityConfig {
  bg: string;
  text: string;
  icon: IconType;
}

const SEVERITY_CONFIG: Record<SeverityLevel, SeverityConfig> = {
  critical: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-800 dark:text-red-200',
    icon: FiXCircle,
  },
  high: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-800 dark:text-orange-200',
    icon: FiAlertTriangle,
  },
  medium: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-800 dark:text-yellow-200',
    icon: FiAlertCircle,
  },
  low: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-800 dark:text-blue-200',
    icon: FiInfo,
  },
};

interface SeverityBadgeProps {
  severity: string;
}

const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity }) => {
  const cfg = SEVERITY_CONFIG[severity as SeverityLevel] ?? SEVERITY_CONFIG.low;
  const Icon = cfg.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}
    >
      <Icon className="w-3 h-3" />
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  );
};

interface SafetyTestConfigProps {
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  selectedTests?: string[];
  onSelectedTestsChange?: (tests: string[]) => void;
  failThreshold?: string;
  onFailThresholdChange?: (threshold: string) => void;
  disabled?: boolean;
}

export const SafetyTestConfig: React.FC<SafetyTestConfigProps> = ({
  enabled = false,
  onEnabledChange,
  selectedTests = ['toxicity', 'pii'],
  onSelectedTestsChange,
  failThreshold = 'high',
  onFailThresholdChange,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(enabled);

  const handleToggle = (checked: boolean) => {
    onEnabledChange?.(checked);
    if (checked) setExpanded(true);
  };

  const toggleTest = (testId: string) => {
    if (selectedTests.includes(testId)) {
      onSelectedTestsChange?.(selectedTests.filter((t) => t !== testId));
    } else {
      onSelectedTestsChange?.([...selectedTests, testId]);
    }
  };

  const headerBg = enabled
    ? 'bg-green-50 dark:bg-green-900/20'
    : 'bg-gray-50 dark:bg-gray-800';

  const shieldColor = enabled
    ? 'text-green-600 dark:text-green-400'
    : 'text-gray-400';

  const testCountSuffix = selectedTests.length === 1 ? '' : 's';
  const safetySubtitle = enabled
    ? `${selectedTests.length} test${testCountSuffix} enabled`
    : 'Disabled';

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className={`px-4 py-3 flex items-center justify-between cursor-pointer transition-colors ${headerBg}`}
        onClick={() => setExpanded(!expanded)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }} role="button" tabIndex={0}
      >
        <div className="flex items-center gap-3">
          <FiShield className={`w-5 h-5 ${shieldColor}`} />
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">Safety Testing</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {safetySubtitle}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label aria-label="Toggle safety testing"
            className="relative inline-flex items-center cursor-pointer"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
          >
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={disabled}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600" />
          </label>
          {expanded ? (
            <FiChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <FiChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && enabled && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Safety Tests to Run
            </span>
            <div className="grid grid-cols-2 gap-2">
              {SAFETY_TEST_TYPES.map((test) => {
                const isSelected = selectedTests.includes(test.id);
                const btnBorder = isSelected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500';

                const labelColor = isSelected
                  ? 'text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300';

                return (
                  <button
                    key={test.id}
                    type="button"
                    onClick={() => toggleTest(test.id)}
                    disabled={disabled}
                    className={`p-3 text-left rounded-lg border transition-all ${btnBorder} ${
                      disabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg"><test.icon /></span>
                      <span className={`font-medium ${labelColor}`}>{test.name}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-7">
                      {test.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fail Threshold
            </span>
            <div className="flex gap-2">
              {SAFETY_FAIL_THRESHOLDS.map((threshold) => {
                const isActive = failThreshold === threshold.value;
                const btnClass = isActive
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700';

                return (
                  <button
                    key={threshold.value}
                    type="button"
                    onClick={() => onFailThresholdChange?.(threshold.value)}
                    disabled={disabled}
                    className={`flex-1 px-3 py-2 text-sm rounded-md border transition-colors ${btnClass} ${
                      disabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {threshold.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {SAFETY_FAIL_THRESHOLDS.find((t) => t.value === failThreshold)?.description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

interface SafetyIssue {
  severity?: string;
  category?: string;
  description?: string;
  message?: string;
  location?: string;
  confidence?: number;
}

interface SafetyTestResult {
  test_type: string;
  passed: boolean;
  issues?: SafetyIssue[];
}

interface SafetyResultsPanelProps {
  results?: SafetyTestResult[];
  passed?: boolean;
  totalIssues?: number;
}

export const SafetyResultsPanel: React.FC<SafetyResultsPanelProps> = ({
  results = [],
  passed = true,
  totalIssues = 0,
}) => {
  const [expandedItems, setExpandedItems] = useState<number[]>([]);

  const toggleExpanded = (index: number) => {
    if (expandedItems.includes(index)) {
      setExpandedItems(expandedItems.filter((i) => i !== index));
    } else {
      setExpandedItems([...expandedItems, index]);
    }
  };

  if (results.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <FiShield className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No safety tests were run</p>
      </div>
    );
  }

  const summaryBorder = passed
    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';

  const summaryTitle = passed
    ? 'text-green-800 dark:text-green-200'
    : 'text-red-800 dark:text-red-200';

  const summarySubtitle = passed
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400';

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <div className={`p-4 rounded-lg border ${summaryBorder}`}>
        <div className="flex items-center gap-3">
          {passed ? (
            <FiCheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          ) : (
            <FiXCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          )}
          <div>
            <h4 className={`font-semibold ${summaryTitle}`}>
              Safety Check {passed ? 'Passed' : 'Failed'}
            </h4>
            <p className={`text-sm ${summarySubtitle}`}>
              {totalIssues} issue{totalIssues === 1 ? '' : 's'} detected across {results.length}{' '}
              test{results.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
      </div>

      {/* Individual Results */}
      <div className="space-y-2">
        {results.map((result, index) => {
          const testType = SAFETY_TEST_TYPES.find((t) => t.id === result.test_type);
          const isExpanded = expandedItems.includes(index);
          const hasIssues = (result.issues?.length ?? 0) > 0;
          const issueCountSuffix = (result.issues?.length ?? 0) === 1 ? '' : 's';
          const issueCountLabel = hasIssues
            ? `${result.issues!.length} issue${issueCountSuffix}`
            : 'No issues';

          return (
            <div
              key={`safety-result-${result.test_type}-${index}`}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              <button
                type="button"
                onClick={() => hasIssues && toggleExpanded(index)}
                className={`w-full px-4 py-3 flex items-center justify-between ${
                  hasIssues
                    ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700'
                    : 'cursor-default'
                }`}
              >
                <div className="flex items-center gap-3">
                  {testType ? <testType.icon className="w-5 h-5" /> : <FiSearch className="w-5 h-5" />}
                  <div className="text-left">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {testType?.name ?? result.test_type}
                    </span>
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                      {issueCountLabel}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {result.passed ? (
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                      <FiCheckCircle className="w-4 h-4" />
                      Passed
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-600 dark:text-red-400 text-sm">
                      <FiXCircle className="w-4 h-4" />
                      Failed
                    </span>
                  )}
                  {hasIssues &&
                    (isExpanded ? (
                      <FiChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <FiChevronDown className="w-4 h-4 text-gray-400" />
                    ))}
                </div>
              </button>

              {/* Expanded Issues */}
              {isExpanded && hasIssues && (
                <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <div className="space-y-2">
                    {result.issues!.map((issue, issueIdx) => (
                      <div
                        key={`issue-${index}-${issueIdx}`}
                        className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <SeverityBadge severity={issue.severity ?? 'medium'} />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {issue.category ?? 'General'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {issue.description ?? issue.message}
                            </p>
                            {issue.location && (
                              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                Location: {issue.location}
                              </p>
                            )}
                          </div>
                          {issue.confidence != null && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {(issue.confidence * 100).toFixed(0)}% confidence
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface SafetySummaryCardProps {
  passed: boolean;
  totalIssues?: number;
  testCount?: number;
  onClick?: () => void;
}

export const SafetySummaryCard: React.FC<SafetySummaryCardProps> = ({
  passed,
  totalIssues = 0,
  testCount = 0,
  onClick,
}) => {
  const cardBorder = passed
    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:border-green-300 dark:hover:border-green-700'
    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:border-red-300 dark:hover:border-red-700';

  const shieldColor = passed
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400';

  const labelColor = passed
    ? 'text-green-800 dark:text-green-200'
    : 'text-red-800 dark:text-red-200';

  return (
    <div
      onClick={onClick} onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined} role="button" tabIndex={0}
      className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${cardBorder}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FiShield className={`w-5 h-5 ${shieldColor}`} />
          <span className={`font-medium ${labelColor}`}>Safety</span>
        </div>
        <div className="flex items-center gap-2">
          {passed ? (
            <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          ) : (
            <FiXCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          )}
        </div>
      </div>
      <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        {totalIssues} issue{totalIssues === 1 ? '' : 's'} / {testCount} test
        {testCount === 1 ? '' : 's'}
      </div>
      <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-500">
        <FiEye className="w-3 h-3" />
        Click to view details
      </div>
    </div>
  );
};

export default SafetyTestConfig;
